import React, { useRef, useState } from "react";
import { m, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  BookOpenCheck,
  Check,
  CheckCircle2,
  CircleAlert,
  ClipboardCheck,
  Clock3,
  Database,
  FileCheck2,
  FileText,
  GraduationCap,
  ListChecks,
  Network,
  ReceiptIndianRupee,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  UserCheck,
  UsersRound,
  Webhook,
} from "lucide-react";
import BrandWordmarkText from "../../components/BrandWordmarkText";
import { DEMO_BOOKING_ROUTE } from "../../components/marketing/navigation";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import { cn } from "../../lib/utils";

const EASE_OUT = [0.23, 1, 0.32, 1];

const HANDOFF_SCENARIOS = Object.freeze([
  Object.freeze({
    id: "admission",
    number: "01",
    eyebrow: "Enquiry → enrolled student",
    title: "A confirmed admission should not start another round of data entry.",
    summary: "Carry one reviewed record into the student's next school workflows.",
    problem: "Re-keying an admitted learner across teams creates avoidable gaps between the original enquiry, the student profile and the setup that follows.",
    response: "Once authorised staff confirm conversion, the admitted lead stays linked to the student profile used for class allocation and fee setup.",
    outcome: "The next team starts with the same reviewed context.",
    path: Object.freeze([
      Object.freeze({
        key: "admission",
        label: "Admission confirmed",
        detail: "Authorised conversion recorded",
        interface: "conversion",
      }),
      Object.freeze({
        key: "student",
        label: "Student profile created",
        detail: "Linked school record",
        interface: "student-record",
      }),
      Object.freeze({
        key: "class",
        label: "Class & section recorded",
        detail: "Academic context continues",
        interface: "class-allocation",
      }),
      Object.freeze({
        key: "fee",
        label: "Ready for fee setup",
        detail: "No invoice created yet",
        interface: "fee-readiness",
      }),
    ]),
  }),
  Object.freeze({
    id: "attendance",
    number: "02",
    eyebrow: "Attendance → responsible follow-up",
    title: "An absence needs context, a school rule and a clear owner.",
    summary: "Keep the attendance signal connected to a role-owned follow-up task.",
    problem: "A mark recorded in isolation gives the next person too little context and makes responsible family follow-up harder to coordinate.",
    response: "Attendance remains tied to class context, configured school rules and the permitted staff role responsible for the next supported task.",
    outcome: "The follow-up keeps its record, owner and evidence together.",
    path: Object.freeze([
      Object.freeze({ key: "attendance", label: "Attendance recorded", detail: "Class-level context", interface: "attendance-register" }),
      Object.freeze({ key: "rule", label: "School rule checked", detail: "Configured boundary", interface: "attendance-rule" }),
      Object.freeze({ key: "owner", label: "Owner identified", detail: "Role-scoped task", interface: "followup-owner" }),
      Object.freeze({ key: "family", label: "Follow-up prepared", detail: "Open call task", interface: "followup-task" }),
    ]),
  }),
  Object.freeze({
    id: "invoice",
    number: "03",
    eyebrow: "Invoice → traceable receipt",
    title: "Finance evidence should stay attached to the invoice it belongs to.",
    summary: "Connect permission-scoped collection, provider evidence and the school record.",
    problem: "When invoices, approvals and provider evidence sit in separate tools, finance teams spend time reconstructing what happened.",
    response: "The school invoice stays linked to permission-scoped collection activity and, when a school-owned gateway is connected, its provider evidence. Settlement timing and final bank credit remain in provider or bank reconciliation.",
    outcome: "Finance can trace the record without inventing a second source of truth.",
    path: Object.freeze([
      Object.freeze({ key: "invoice", label: "School invoice", detail: "Invoice-led collection", interface: "school-invoice" }),
      Object.freeze({ key: "review", label: "Authorised review", detail: "Permission boundary", interface: "finance-review" }),
      Object.freeze({ key: "evidence", label: "Provider evidence", detail: "Configured gateway event", interface: "provider-evidence" }),
      Object.freeze({ key: "receipt", label: "Traceable record", detail: "Receipt and reporting", interface: "finance-trace" }),
    ]),
  }),
]);

const TONE_STYLES = Object.freeze({
  accent: "border-primary/25 bg-primary/10 text-ink",
  success: "border-success/25 bg-success/10 text-ink",
  warning: "border-orange/25 bg-orange/10 text-ink",
  neutral: "border-line bg-muted text-ink-2",
});

const TONE_DOTS = Object.freeze({
  accent: "bg-accent",
  success: "bg-success",
  warning: "bg-orange",
  neutral: "bg-ink-subtle",
});

function MiniBadge({ children, tone = "accent" }) {
  return (
    <span className={cn(
      "inline-flex min-h-4 items-center gap-1 rounded-full border px-1.5 text-[8px] font-bold leading-none tracking-[0.02em]",
      TONE_STYLES[tone] || TONE_STYLES.accent,
    )}>
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", TONE_DOTS[tone] || TONE_DOTS.accent)} aria-hidden="true" />
      {children}
    </span>
  );
}

function MiniWindow({ title, eyebrow, Icon, status, statusTone = "accent", children, stepKey }) {
  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-[0.78rem] border border-line bg-canvas text-left shadow-[0_10px_28px_-22px_rgba(2,12,35,0.55)]"
      data-testid={`why-handoff-interface-${stepKey}`}
    >
      <div className="flex h-7 items-center justify-between gap-1.5 border-b border-line bg-surface px-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="grid h-4 w-4 shrink-0 place-items-center rounded-[0.3rem] bg-primary/10 text-accent" aria-hidden="true">
            <Icon className="h-2.5 w-2.5" strokeWidth={2} />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[8px] font-bold uppercase leading-none tracking-[0.08em] text-ink-subtle">{eyebrow}</span>
            <span className="mt-0.5 block truncate text-[9px] font-bold leading-none text-ink sm:text-[10px]">{title}</span>
          </span>
        </span>
        {status ? <MiniBadge tone={statusTone}>{status}</MiniBadge> : null}
      </div>
      <div className="h-[calc(100%_-_1.75rem)] p-2">{children}</div>
      <span className="pointer-events-none absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" aria-hidden="true" />
    </div>
  );
}

function MiniKeyValue({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-1.5 border-b border-line/70 py-1 last:border-b-0">
      <span className="truncate text-[8px] leading-none text-ink-subtle sm:text-[9px]">{label}</span>
      <span className={cn(
        "truncate text-[8px] font-bold leading-none sm:text-[9px]",
        "text-ink",
      )}>
        {value}
      </span>
    </div>
  );
}

function MiniCheckRow({ children, muted = false }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 py-0.5">
      <span className={cn(
        "grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full",
        muted ? "bg-muted text-ink-subtle" : "bg-success/10 text-success",
      )} aria-hidden="true">
        {muted ? <Clock3 className="h-2 w-2" /> : <Check className="h-2 w-2" strokeWidth={2.5} />}
      </span>
      <span className={cn("truncate text-[8px] font-medium leading-tight sm:text-[9px]", muted ? "text-ink-subtle" : "text-ink-2")}>{children}</span>
    </div>
  );
}

function HandoffMicroInterface({ step }) {
  switch (step.interface) {
    case "conversion":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Admissions" title="Lead conversion" Icon={UserCheck} status="Confirmed" statusTone="success">
          <div className="rounded-lg border border-success/20 bg-success/10 px-2 py-1.5">
            <div className="flex items-center gap-1.5 text-[9px] font-bold text-ink sm:text-[10px]">
              <BadgeCheck className="h-3 w-3 text-success" aria-hidden="true" />
              Conversion recorded
            </div>
          </div>
          <div className="mt-1">
            <MiniKeyValue label="Lead status" value="Admitted" />
            <MiniKeyValue label="Next record" value="Linked" />
          </div>
        </MiniWindow>
      );
    case "student-record":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Students" title="Student record" Icon={GraduationCap} status="Active" statusTone="success">
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/10 font-mono text-[9px] font-bold text-ink">S42</span>
            <div className="min-w-0">
              <p className="truncate text-[9px] font-bold text-ink sm:text-[10px]">Student 042</p>
              <p className="mt-0.5 truncate text-[8px] text-ink-subtle">Synthetic record</p>
            </div>
          </div>
          <div className="mt-1.5 flex gap-1 border-b border-line pb-1">
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[8px] font-bold text-ink">Profile</span>
            <span className="px-1 py-0.5 text-[8px] text-ink-subtle">Documents</span>
          </div>
          <MiniCheckRow>Source record linked</MiniCheckRow>
        </MiniWindow>
      );
    case "class-allocation":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Academics" title="Class allocation" Icon={BookOpenCheck} status="Recorded" statusTone="success">
          <p className="text-[8px] font-bold uppercase tracking-[0.08em] text-ink-subtle">Academic placement</p>
          <div className="mt-1.5 grid grid-cols-[1fr_auto_1fr] items-center gap-1">
            <div className="rounded-md border border-line bg-surface px-1.5 py-1.5">
              <span className="block text-[8px] text-ink-subtle">Class</span>
              <span className="mt-0.5 block text-[9px] font-bold text-ink">6</span>
            </div>
            <ArrowRight className="h-2.5 w-2.5 text-accent" aria-hidden="true" />
            <div className="rounded-md border border-line bg-surface px-1.5 py-1.5">
              <span className="block text-[8px] text-ink-subtle">Section</span>
              <span className="mt-0.5 block text-[9px] font-bold text-ink">B</span>
            </div>
          </div>
          <p className="mt-1.5 flex items-center gap-1 text-[8px] font-semibold text-ink-2"><Check className="h-2.5 w-2.5 text-success" /> Class context saved</p>
        </MiniWindow>
      );
    case "fee-readiness":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Fees" title="Setup readiness" Icon={ListChecks} status="Review" statusTone="warning">
          <MiniCheckRow>Student context available</MiniCheckRow>
          <MiniCheckRow>Class and section available</MiniCheckRow>
          <MiniCheckRow muted>No invoice created</MiniCheckRow>
          <div className="mt-1 rounded-md border border-orange/25 bg-orange/10 px-1.5 py-1 text-[8px] font-bold text-ink">Ready for fee review</div>
        </MiniWindow>
      );
    case "attendance-register":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Attendance" title="Class register" Icon={ClipboardCheck} status="Saved" statusTone="success">
          <div className="flex items-center justify-between border-b border-line pb-1">
            <span className="text-[8px] font-bold text-ink sm:text-[9px]">Class 6 · Section B</span>
            <span className="text-[8px] text-ink-subtle">Today</span>
          </div>
          <div className="mt-1 space-y-1">
            {["Student 042", "Student 043"].map((student, index) => (
              <div key={student} className="grid grid-cols-[1fr_auto_auto] items-center gap-1 text-[8px]">
                <span className="truncate text-ink-2">{student}</span>
                <span className={cn("rounded px-1 py-0.5 font-bold text-ink", index ? "bg-orange/10" : "bg-success/10")}>{index ? "A" : "P"}</span>
                <Check className="h-2.5 w-2.5 text-success" aria-hidden="true" />
              </div>
            ))}
          </div>
          <p className="mt-1.5 text-[8px] font-semibold text-ink">Register saved</p>
        </MiniWindow>
      );
    case "attendance-rule":
      return (
        <MiniWindow stepKey={step.key} eyebrow="School rule" title="Attendance check" Icon={SlidersHorizontal} status="Configured">
          <div className="space-y-1">
            <div className="rounded-md border border-line bg-surface px-1.5 py-1">
              <span className="text-[8px] font-bold uppercase tracking-[0.08em] text-ink">If</span>
              <p className="mt-0.5 truncate text-[8px] font-semibold text-ink sm:text-[9px]">Saved mark is absent</p>
            </div>
            <div className="rounded-md border border-line bg-surface px-1.5 py-1">
              <span className="text-[8px] font-bold uppercase tracking-[0.08em] text-ink">Then</span>
              <p className="mt-0.5 truncate text-[8px] font-semibold text-ink sm:text-[9px]">Prepare permitted follow-up</p>
            </div>
          </div>
        </MiniWindow>
      );
    case "followup-owner":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Tasks" title="Follow-up owner" Icon={UserCheck} status="Assigned" statusTone="success">
          <div className="flex items-center gap-2 rounded-lg border border-line bg-surface p-1.5">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-accent"><UsersRound className="h-3.5 w-3.5" /></span>
            <div className="min-w-0">
              <p className="truncate text-[9px] font-bold text-ink">Teacher role</p>
              <p className="mt-0.5 truncate text-[8px] text-ink-subtle">Class 6 · Section B</p>
            </div>
          </div>
          <div className="mt-1.5 flex items-center gap-1 text-[8px] font-semibold text-ink-2"><ShieldCheck className="h-2.5 w-2.5 text-accent" /> Role scope checked</div>
        </MiniWindow>
      );
    case "followup-task":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Task management" title="Absence follow-up" Icon={ListChecks} status="Open" statusTone="warning">
          <div className="rounded-lg border border-line bg-surface p-1.5">
            <div className="flex items-center justify-between gap-1">
              <span className="text-[8px] font-bold text-ink sm:text-[9px]">Call guardian</span>
              <span className="text-[8px] text-ink-subtle">Teacher role</span>
            </div>
            <div className="mt-1 space-y-1" aria-hidden="true">
              <span className="block h-1 rounded-full bg-muted" />
              <span className="block h-1 w-4/5 rounded-full bg-muted" />
            </div>
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-1 text-[8px]"><span className="font-bold text-ink">Task queued</span><span className="text-ink-subtle">No message sent</span></div>
        </MiniWindow>
      );
    case "school-invoice":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Fee & finance" title="School invoice" Icon={ReceiptIndianRupee} status="Issued">
          <div className="flex items-start justify-between gap-2 border-b border-line pb-1.5">
            <div><p className="text-[9px] font-bold text-ink">INV-SAMPLE-042</p><p className="mt-0.5 text-[8px] text-ink-subtle">Synthetic invoice</p></div>
            <FileText className="h-3.5 w-3.5 text-accent" aria-hidden="true" />
          </div>
          <MiniKeyValue label="Fee structure" value="Linked" />
          <MiniKeyValue label="Amount" value="Recorded" />
        </MiniWindow>
      );
    case "finance-review":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Finance control" title="Collection access" Icon={ShieldCheck} status="Checked" statusTone="success">
          <MiniCheckRow>Invoice record linked</MiniCheckRow>
          <MiniCheckRow>Role permission checked</MiniCheckRow>
          <MiniCheckRow>Provider step permitted</MiniCheckRow>
          <p className="mt-1 border-t border-line pt-1 text-[8px] text-ink-subtle">Access remains role-scoped</p>
        </MiniWindow>
      );
    case "provider-evidence":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Connected gateway" title="Provider event" Icon={Webhook} status="Matched" statusTone="success">
          <div className="relative ml-1 border-l border-primary/25 pl-2">
            {["Callback received", "Matched to invoice", "Settlement separate"].map((event, index) => (
              <div key={event} className="relative flex min-h-[1.1rem] items-center text-[8px] text-ink-2">
                <span className={cn("absolute -left-[0.68rem] h-1.5 w-1.5 rounded-full ring-2 ring-canvas", index < 2 ? "bg-success" : "bg-muted")} aria-hidden="true" />
                <span className="truncate">{event}</span>
              </div>
            ))}
          </div>
          <p className="mt-1 text-[8px] font-semibold text-ink-2">Evidence stored against invoice</p>
        </MiniWindow>
      );
    case "finance-trace":
      return (
        <MiniWindow stepKey={step.key} eyebrow="Audit trail" title="Traceable record" Icon={ScrollText} status="Retained" statusTone="success">
          <div className="space-y-1">
            {[
              ["Invoice issued", FileText],
              ["Provider event logged", Database],
              ["Receipt recorded", FileCheck2],
            ].map(([event, EventIcon]) => (
              <div key={event} className="flex items-center gap-1.5 rounded-md bg-surface px-1.5 py-1 text-[8px] font-medium text-ink-2">
                <EventIcon className="h-2.5 w-2.5 shrink-0 text-accent" aria-hidden="true" />
                <span className="truncate">{event}</span>
                <Check className="ml-auto h-2.5 w-2.5 shrink-0 text-success" aria-hidden="true" />
              </div>
            ))}
          </div>
        </MiniWindow>
      );
    default:
      return null;
  }
}

function ScenarioPath({ scenario, animateSelection }) {

  return (
    <div
      className="relative mt-6 sm:mt-9"
      data-testid="connected-school-core"
      data-evidence-layout="product-micro-ui"
    >
      <svg
        className="pointer-events-none absolute inset-x-[8%] top-[4.45rem] h-[12.5rem] w-[84%] text-primary max-[359px]:hidden sm:hidden"
        viewBox="0 0 1000 220"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M250 22 H750 M250 198 H750" fill="none" stroke="currentColor" strokeOpacity="0.24" strokeWidth="2" />
        <path d="M750 42 V88 Q750 110 728 110 H272 Q250 110 250 132 V178" fill="none" stroke="currentColor" strokeDasharray="8 10" strokeOpacity="0.22" strokeWidth="2" />
        <path d="m490 14 10 8-10 8M242 166l8 10 8-10M490 190l10 8-10 8" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.7" strokeWidth="2" />
      </svg>
      <svg
        className="pointer-events-none absolute left-[8%] right-[8%] top-[4.5rem] hidden h-8 w-[84%] text-primary sm:block xl:top-20"
        viewBox="0 0 1000 80"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
        data-testid="connected-school-connectors"
      >
        <path d="M4 40 H996" fill="none" stroke="currentColor" strokeOpacity="0.26" strokeWidth="2" />
        <path d="m323 31 10 9-10 9M656 31l10 9-10 9" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.72" strokeWidth="2" />
      </svg>

      <ol className="relative grid grid-cols-1 gap-x-3 gap-y-6 min-[360px]:grid-cols-2 sm:grid-cols-4 sm:gap-3" aria-label={`${scenario.eyebrow} connected hand-off`}>
        {scenario.path.map((step, index) => (
          <m.li
            key={step.key}
            className="relative z-10 min-w-0 text-center"
            data-testid={`connected-school-node-${step.key}`}
            initial={animateSelection ? { opacity: 0, transform: "translate3d(0, 7px, 0)" } : false}
            animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
            transition={{ duration: animateSelection ? 0.18 : 0, delay: animateSelection ? index * 0.035 : 0, ease: EASE_OUT }}
          >
            <div
              className="why-handoff-glass relative mx-auto grid h-[9.25rem] w-full max-w-[11rem] place-items-center overflow-hidden rounded-[1.05rem] border border-line/80 bg-surface/80 p-1.5 shadow-[0_24px_54px_-38px_rgba(2,12,35,0.72)] backdrop-blur-xl supports-[backdrop-filter]:bg-surface/72 min-[360px]:h-[8.75rem] min-[360px]:max-w-[9rem] sm:h-[9.25rem] sm:max-w-[9.75rem] xl:h-40 xl:max-w-[11rem]"
              data-testid={`why-handoff-evidence-${step.key}`}
              data-evidence-kind="product-micro-ui"
            >
              <HandoffMicroInterface step={step} />
              <span className="absolute left-0 top-0 grid h-5 min-w-5 place-items-center rounded-full bg-accent px-1 font-mono text-[9px] font-bold text-accent-fg shadow-sm">
                {String(index + 1).padStart(2, "0")}
              </span>
            </div>
            <span className="mt-3 block min-w-0 sm:mt-3.5">
              <span className="block font-heading text-xs font-bold leading-snug text-ink sm:text-sm">{step.label}</span>
              <span className="mt-1 block text-[10.5px] leading-relaxed text-ink-subtle sm:text-xs">{step.detail}</span>
            </span>
          </m.li>
        ))}
      </ol>
    </div>
  );
}

function ScenarioStage({ scenario, animateSelection }) {
  return (
    <m.div
      key={scenario.id}
      id="why-handoff-panel"
      role="tabpanel"
      aria-labelledby={`why-handoff-tab-${scenario.id}`}
      initial={animateSelection ? { opacity: 0, transform: "translate3d(0, 7px, 0)" } : false}
      animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
      transition={{ duration: animateSelection ? 0.2 : 0, ease: EASE_OUT }}
      className="relative min-w-0 px-1 py-6 sm:px-7 sm:py-10 lg:px-10 lg:py-11"
      data-testid={`why-handoff-panel-${scenario.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line pb-4 sm:gap-4 sm:pb-5">
        <div className="flex min-w-0 items-center gap-3" data-testid="why-platform-art">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-primary/20 bg-primary/10 text-accent sm:h-12 sm:w-12" aria-hidden="true">
            <Network className="h-5 w-5 sm:h-6 sm:w-6" />
          </span>
          <div className="min-w-0">
            <BrandWordmarkText className="text-[1.05rem] sm:text-[1.18rem]" />
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-subtle">Connected hand-off</p>
          </div>
        </div>
        <span className="font-mono text-xs font-semibold tracking-[0.14em] text-accent">{scenario.number} / 03</span>
      </div>

      <div className="pt-5 sm:pt-6">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-2">
          <CircleAlert className="h-3.5 w-3.5 text-orange" aria-hidden="true" />
          The broken hand-off
        </p>
        <h3 className="mt-2.5 max-w-[26ch] font-heading text-xl font-bold leading-tight tracking-[-0.025em] text-ink sm:mt-3 sm:text-[2rem]">
          {scenario.title}
        </h3>
        <p className="mt-2.5 max-w-[72ch] text-[13px] leading-relaxed text-ink-2 sm:mt-3 sm:text-[15px]">{scenario.problem}</p>
      </div>

      <ScenarioPath scenario={scenario} animateSelection={animateSelection} />

      <dl className="mt-6 grid border-y border-line sm:mt-8 sm:grid-cols-[1.45fr_1fr]">
        <div className="py-4 sm:py-5 sm:pr-7">
          <dt className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-accent">
            <Network className="h-3.5 w-3.5" aria-hidden="true" />
            Connected response
          </dt>
          <dd className="mt-2 text-[13px] leading-relaxed text-ink-2 sm:text-sm">{scenario.response}</dd>
        </div>
        <div className="border-t border-line py-4 sm:border-l sm:border-t-0 sm:py-5 sm:pl-7">
          <dt className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden="true" />
            Operational outcome
          </dt>
          <dd className="mt-2 font-heading text-[13px] font-bold leading-relaxed text-ink sm:text-sm">{scenario.outcome}</dd>
        </div>
      </dl>
    </m.div>
  );
}

/**
 * A disclosed, illustrative set of school hand-offs. Every evidence state is rendered as
 * code-native product micro-UI, with synthetic labels and critical meaning in adjacent HTML.
 */
export default function ConnectedSchoolConceptVisual({ className = "", onRequestTour }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const interactionMode = useRef("initial");
  const tabRefs = useRef([]);
  const reducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const motionOff = Boolean(reducedMotion || paused);
  const activeScenario = HANDOFF_SCENARIOS[activeIndex];

  const selectScenario = (index, mode) => {
    interactionMode.current = mode;
    setActiveIndex(index);
  };

  const handleTabKeyDown = (event, index) => {
    let nextIndex = null;
    if (["ArrowRight", "ArrowDown"].includes(event.key)) nextIndex = (index + 1) % HANDOFF_SCENARIOS.length;
    if (["ArrowLeft", "ArrowUp"].includes(event.key)) nextIndex = (index - 1 + HANDOFF_SCENARIOS.length) % HANDOFF_SCENARIOS.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = HANDOFF_SCENARIOS.length - 1;
    if (nextIndex === null) {
      if (["Enter", " "].includes(event.key)) interactionMode.current = "keyboard";
      return;
    }
    event.preventDefault();
    selectScenario(nextIndex, "keyboard");
    tabRefs.current[nextIndex]?.focus();
  };

  const animateSelection = !motionOff && interactionMode.current === "pointer";

  return (
    <figure
      className={cn("relative min-w-0 text-ink", className)}
      aria-labelledby="connected-school-concept-title"
      data-testid="connected-school-concept"
      data-conceptual="true"
      data-rendering="product-micro-ui"
      data-motion-state={motionOff ? "off" : "on"}
      data-interaction-mode={interactionMode.current}
    >
      <figcaption className="flex flex-col gap-2 border-y border-line py-3.5 sm:flex-row sm:items-center sm:justify-between sm:py-4">
        <div className="min-w-0">
          <p id="connected-school-concept-title" className="text-xs font-bold uppercase tracking-[0.14em] text-accent">
            Three school hand-offs, one operating context
          </p>
          <p className="mt-1 text-sm font-semibold text-ink">Choose a real workflow to see where disconnected tools lose context.</p>
        </div>
        <span className="inline-flex w-fit items-center text-[10px] font-bold uppercase tracking-[0.12em] text-ink-subtle">
          Illustrative workflow UI · synthetic sample data · not product UI or a live customer screen
        </span>
      </figcaption>

      <div className="relative grid min-w-0 lg:grid-cols-[minmax(15.5rem,0.72fr)_minmax(0,1.75fr)]">
        <div className="border-b border-line py-3 lg:border-b-0 lg:border-r lg:py-7">
          <p className="px-1 pb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-subtle lg:px-0">Explore the hand-off</p>
          <div role="tablist" aria-label="Connected school hand-off scenarios" className="divide-y divide-line border-y border-line">
            {HANDOFF_SCENARIOS.map((scenario, index) => {
              const selected = index === activeIndex;
              const instant = motionOff || interactionMode.current === "keyboard";
              return (
                <button
                  key={scenario.id}
                  ref={(node) => { tabRefs.current[index] = node; }}
                  type="button"
                  role="tab"
                  id={`why-handoff-tab-${scenario.id}`}
                  aria-controls="why-handoff-panel"
                  aria-selected={selected}
                  tabIndex={selected ? 0 : -1}
                  onPointerDown={() => { interactionMode.current = "pointer"; }}
                  onClick={() => selectScenario(index, interactionMode.current === "keyboard" ? "keyboard" : "pointer")}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                  className={cn(
                    "group relative min-h-11 w-full py-3 pl-5 pr-3 text-left focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focusring/60 sm:py-4 lg:py-5 lg:pl-6",
                    selected ? "bg-primary/[0.055]" : "bg-transparent",
                  )}
                  data-testid={`why-handoff-tab-${scenario.id}`}
                >
                  <span className={cn("absolute inset-y-0 left-0 w-0.5 bg-accent transition-opacity", instant ? "duration-0" : "duration-200", selected ? "opacity-100" : "opacity-0")} aria-hidden="true" />
                  <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.13em] text-accent">
                    <span className="font-mono">{scenario.number}</span>
                    {scenario.eyebrow}
                  </span>
                  <span className="mt-2 hidden font-heading text-sm font-bold leading-snug text-ink sm:block">{scenario.summary}</span>
                </button>
              );
            })}
          </div>
          <p className="px-1 pt-4 text-xs leading-relaxed text-ink-subtle lg:px-0">
            No automated decisions are implied. Material actions remain permission-scoped and human-confirmed.
          </p>
        </div>

        <div className="relative min-w-0 overflow-hidden bg-gradient-to-br from-primary/[0.055] via-transparent to-orange/[0.045] lg:ml-7">
          <div
            className="pointer-events-none absolute inset-0 opacity-45"
            style={{
              backgroundImage: "linear-gradient(rgb(var(--vp-border-subtle) / 0.45) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--vp-border-subtle) / 0.45) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
              maskImage: "linear-gradient(to bottom, black, transparent 82%)",
              WebkitMaskImage: "linear-gradient(to bottom, black, transparent 82%)",
            }}
            aria-hidden="true"
          />
          <ScenarioStage scenario={activeScenario} animateSelection={animateSelection} />
        </div>
      </div>

      <div className="grid gap-4 border-y border-line py-5 sm:px-1 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <div className="min-w-0">
          <p className="font-heading text-sm font-bold text-ink">Bring one real school hand-off to the demo.</p>
          <p className="mt-1 max-w-[64ch] text-xs leading-relaxed text-ink-subtle">
            We will map its records, rules, owners and exceptions. Product-tour access follows the reviewed demo request.
          </p>
        </div>
        <Link
          to={DEMO_BOOKING_ROUTE}
          onClick={onRequestTour}
          data-testid="why-request-product-tour"
          className="group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-accent px-5 text-sm font-semibold text-accent-fg transition-transform duration-200 ease-premium active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60 md:w-auto"
        >
          Book a live demo
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </figure>
  );
}

export { HANDOFF_SCENARIOS };
